import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  useTheme,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { hasCrudPermission, hasCrudOrAllPermission } from "../../Utils/organizationPermissions";
import NotAllowed from "../../Exceptions/NotAllowed";

const Layout1 = ({
  loading,
  heading,
  Route,
  btnName,
  Data,
  totalCount,
  fetchData,
  page,
  perPage,
  setPage,
  setPerPage,
  onSearch,
  search,
  setSearch,
  columns,
  messages,
  DeleteFunc,
  showActions = true,
  showAddButton = true,
  add_action,
  edit_action,
  delete_action,
  deleteConfirmTitle,
  deleteConfirmMessage,
}) => {
  const navigate = useNavigate();
  const theme = useTheme(); // 🌙 Detect MUI dark/light mode
  const { Permission } = usePermissionDataStore();
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [showNotAllowed, setShowNotAllowed] = useState(false);

  const hasAddPermission = useMemo(() => {
    if (!add_action) return false;
    return hasCrudOrAllPermission(Permission, add_action);
  }, [add_action, Permission]);

  const hasEditPermission = useMemo(() => {
    if (!edit_action) return false;
    return hasCrudOrAllPermission(Permission, edit_action);
  }, [edit_action, Permission]);

  const hasDeletePermission = useMemo(() => {
    if (!delete_action) return false;
    return hasCrudOrAllPermission(Permission, delete_action);
  }, [delete_action, Permission]);

  const handleDelete = async () => {
    if (!hasDeletePermission) {
      setDeleteDialog(null);
      setShowNotAllowed(true);
      return;
    }
    await DeleteFunc(deleteDialog).then(() => {
      fetchData(true); // refetch updated list
      toast.success(`${messages?.[3] || "Item"} deleted`);
    }).catch(() => {
      toast.error("Delete failed");
    });
    setDeleteDialog(null);


  };


  const finalColumns = [
    ...columns,
    ...(showActions
      ? [
        {
          field: "actions",
          headerName: "Actions",
          flex: 1,
          sortable: false,
          resizable: false,          // Not natively supported by MUI DataGrid, okay to leave
          align: "center",
          headerAlign: "center",
          disableColumnMenu: true,   // ✅ Hides the three-dot menu in the header
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                }}
              >
                {hasEditPermission && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "transparent",
                        color: "#424242",
                        borderRadius: "8px",
                        p: 1,
                      }}
                      onClick={() => navigate(`${Route}/edit/${params.row.id}`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {hasDeletePermission && DeleteFunc && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: "transparent",
                        color: "#424242",
                        borderRadius: "8px",
                        p: 1,
                      }}
                      onClick={() => setDeleteDialog(params.row.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

              </Box>
            </Box>
          ),

        }



      ]
      : []),
  ];

  const isDarkMode = theme.palette.mode === "dark";

  if (showNotAllowed) {
    return <NotAllowed />;
  }

  return (
    <Box p={4}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{heading}</Typography>
        {showAddButton && btnName && hasAddPermission && Route && (
          <Button variant="contained" onClick={() => navigate(`${Route}/add`)}>
            {btnName}
          </Button>
        )}
      </Box>

      {/* Search */}
      <TextField
        placeholder="Search..."
        fullWidth
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      />

      {/* Table & Loader */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
            }}
          >
            <DataGrid
              rows={Data}
              columns={finalColumns}
              paginationModel={{ page: page, pageSize: perPage }}
              onPaginationModelChange={({ page, pageSize }) => {
                setPage(page);
                setPerPage(pageSize);
              }}
              rowCount={totalCount}
              paginationMode="server"
              autoHeight
              disableSelectionOnClick
              columnBuffer={6}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: perPage },
                },
              }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#e6f0ff",
                  fontWeight: "bold",
                },
                "& .even": {
                  backgroundColor: isDarkMode ? "#2a2a2a" : "#f9f9f9",
                },
                "& .odd": {
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                },
              }}
              componentsProps={{
                columnHeaders: {
                  draggable: true,
                },
              }}
              experimentalFeatures={{ columnGrouping: true }}
            />
          </Box>


        </>
      )}

      {/* Delete Dialog */}
      <Dialog
        open={!!deleteDialog}
        onClose={() => setDeleteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: deleteConfirmMessage ? "error.main" : "inherit" }}>
          {deleteConfirmTitle || "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          {deleteConfirmMessage ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {deleteConfirmMessage}
              </Alert>
              <Typography>
                Are you sure you want to delete this {messages?.[3]}? This cannot be undone.
              </Typography>
            </>
          ) : (
            <>Are you sure you want to delete this {messages?.[3]}?</>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout1;
